import React, { useState } from 'react';
import {
  FDAccordion,
  FDAccordionDetails,
  FDAccordionTitle,
  FDBodyText,
  FDBox,
  FDIcon,
  FDPaper,
  FDStack,
  FDTitleText,
  FDTooltip,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import ManagedAccountPortfolioPieChart
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/AccountPerformanceDashboard/PortfolioSection/ManagedAccountPortfolioPieChart';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  HoldingsSectionForScenarioSelectionModelUpdate,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionRedefined/ScenarioSelectionModelDetailsStepTemplate/HoldingsSectionForScenarioSelectionModelUpdate';
import fr from 'facet-rest';
import useMobileLayout from 'Hooks/useMobileLayout';
import {
  InvestmentReturnsSectionForScenarioSelectionModelUpdate,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionRedefined/ScenarioSelectionModelDetailsStepTemplate/InvestmentReturnsSectionForScenarioSelectionModelUpdate';
import {
  MaxLossesAndReturnsSectionForScenarioSelectionModelUpdate,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionRedefined/ScenarioSelectionModelDetailsStepTemplate/MaxLossesAndReturnsSectionForScenarioSelectionModelUpdate';
import { dataAttrs } from 'Helpers/Testing';

const convertChartDataCollectionToObjectArray = (chartDataCollection) => {
  if (!chartDataCollection.resources().list()) {
    return [];
  }
  // Filter out values that are non numeric. This is just a guardrail to avoid unplugs incase the server returns something unexpected
  const filteredChartData = chartDataCollection.resources().list().filter(data => {
    const numValue = Number(data.attr('value'));
    return !Number.isNaN(numValue);
  });
  return filteredChartData.map((res) => ({
    color: res.attr('color'),
    label: res.attr('label'),
    name: res.attr('label'), // Using label as the name intentionally
    type: res.attr('type'),
    value: Number(res.attr('value')),
  }));
};

export const ScenarioSelectionModelDetailsStepContent = () => {
  const { resource } = useMetaLayoutContext();
  const isMobile = useMobileLayout();
  const [openSection, setOpenSection] = useState(null);

  const allocationMixChartData = convertChartDataCollectionToObjectArray(resource.attr('allocationClassificationData'));
  const tickerMixChartData = convertChartDataCollectionToObjectArray(resource.attr('tickerClassificationData'));

  const handleAccordionChange = (section = '') => {
    if (section === openSection) {
      setOpenSection(null);
      return;
    }
    setOpenSection(section);
  };

  return (
    <FDStack width={'100%'} justifyContent={'center'} {...dataAttrs('model-details')}>
      <FDBox maxWidth={1020}>
        <FDStack direction={'column'} spacing={3} width={'100%'} padding={isMobile ? 0 : 2}>
          <MetaControlField attr={'title'} />
          <FDPaper width={'100%'} padding={2} {...dataAttrs('investment-mix-section')}>
            <FDStack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} spacing={isMobile ? 2 : 6}>
              <FDStack justifyContent={'center'} width={'100%'} maxWidth={400} minHeight={isMobile ? 300 : 250} flexShrink={1}>
                <ManagedAccountPortfolioPieChart allocationModelClassifications={allocationMixChartData} tickerClassifications={tickerMixChartData} />
              </FDStack>
              <FDStack direction={'column'} spacing={2}>
                <MetaControlField attr={'investmentMixSectionTitle'} />
                <MetaControlField attr={'investmentMixSectionDescription'} />
                {resource.withRelationship(fr.relationships.equityHoldings.name,
                  (rel) => (
                    <HoldingsSectionForScenarioSelectionModelUpdate
                      holdingsTypeLabel={'Stock Holdings'}
                      holdingsPercentage={resource.attr('equityHoldings')}
                      holdingsCollection={rel.getRelated()}
                      color={'primary'}
                    />
                  ),
                  () => null)}
                {resource.withRelationship(fr.relationships.alternativeIncomeHoldings.name,
                  (rel) => (
                    <HoldingsSectionForScenarioSelectionModelUpdate
                      holdingsTypeLabel={'Alternative Income Holdings'}
                      holdingsPercentage={resource.attr('alternativeIncomeHoldings')}
                      holdingsCollection={rel.getRelated()}
                      color={'purple'}
                    />
                  ),
                  () => null)}
                {resource.withRelationship(fr.relationships.fixedHoldings.name,
                  (rel) => (
                    <HoldingsSectionForScenarioSelectionModelUpdate
                      holdingsTypeLabel={'Bond Holdings'}
                      holdingsPercentage={resource.attr('fixedHoldings')}
                      holdingsCollection={rel.getRelated()}
                      color={'success'}
                    />
                  ),
                  () => null)}
                <FDStack direction={'row'} spacing={1}>
                  <MetaControlField attr={'expenseRatio'} />
                  {resource.attr('expenseRatio') !== undefined && (
                  <FDTooltip
                    title={resource.attr('expenseRatioDescription')}
                  >
                    <FDIcon color={'primary'} icon={'info'} />
                  </FDTooltip>
                  )}
                </FDStack>
              </FDStack>
            </FDStack>
          </FDPaper>
          {/* Spacer for desktop */}
          {!isMobile && (<FDBox height={56} />)}
          <MetaControlField attr={'performanceSectionTitle'} />
          <FDPaper width={'100%'} padding={1} {...dataAttrs('investment-returns-section')}>
            <FDAccordion expanded={openSection === 'returns'} onChange={() => handleAccordionChange('returns')}>
              <FDAccordionTitle>
                <FDStack direction={'row'} spacing={1}>
                  <FDIcon icon={'cashflow'} />
                  <FDTitleText size={'lg'} value={'Investment Returns'} />
                </FDStack>
              </FDAccordionTitle>
              <FDAccordionDetails>
                {/* Checks for both the modelPerformanceChartData and modelPerformanceData relationships before rendering */}
                {resource.withRelationship(
                  fr.relationships.modelPerformanceChartData.name,
                  (modelPerformanceChartDataRel) => resource.withRelationship(
                    fr.relationships.modelPerformanceData.name,
                    (modelPerformanceDataRel) => (
                      <InvestmentReturnsSectionForScenarioSelectionModelUpdate
                        modelPerformanceChartData={modelPerformanceChartDataRel.getRelated()}
                        modelPerformanceData={modelPerformanceDataRel.getRelated()}
                      />
                    ),
                    () => null,
                  ),
                  () => null,
                )}
              </FDAccordionDetails>
            </FDAccordion>
          </FDPaper>
          <FDPaper width={'100%'} padding={1} {...dataAttrs('max-returns-and-losses-section')}>
            <FDAccordion expanded={openSection === 'maxLossesAndReturns'} onChange={() => handleAccordionChange('maxLossesAndReturns')}>
              <FDAccordionTitle>
                <FDStack direction={'row'} spacing={1}>
                  <FDIcon icon={'cashflow'} />
                  <FDTitleText size={'lg'} value={'Maximum index returns & losses'} />
                </FDStack>
              </FDAccordionTitle>
              <FDAccordionDetails>
                <FDStack direction={'column'} spacing={1}>
                  <FDBodyText value={'This section illustrates the potential range of returns and losses based on historical market data for similar investment mixes, helping to understand the potential volatility.'} />
                  {resource.withRelationship(fr.relationships.modelPerformanceData.name,
                    (rel) => {
                      const [modelPerformanceData] = rel.getRelated();
                      if (!modelPerformanceData) return null;

                      return (
                        <MaxLossesAndReturnsSectionForScenarioSelectionModelUpdate
                          oneYearMaxLoss={modelPerformanceData.attr('oneYearMaxLoss')}
                          threeYearMaxAnnualizedLosses={modelPerformanceData.attr('threeYearMaxAnnualizedLosses')}
                          averageAnnualReturn={modelPerformanceData.attr('averageAnnualReturn')}
                          threeYearMaxAnnualizedReturn={modelPerformanceData.attr('threeYearMaxAnnualizedReturn')}
                          oneYearMaxReturn={modelPerformanceData.attr('oneYearMaxReturn')}
                          disclaimer={modelPerformanceData.attr('maxLossesAndReturnsDisclaimer')}
                          disclaimerReadMore={modelPerformanceData.attr('maxLossesAndReturnsDisclaimerReadMore')}
                        />
                      );
                    },
                    () => null)}
                </FDStack>
              </FDAccordionDetails>
            </FDAccordion>
          </FDPaper>
        </FDStack>
      </FDBox>
    </FDStack>
  );
};

ScenarioSelectionModelDetailsStepContent.propTypes = {};
